<template>
  <v-group
    ref="fieldGroup"
    :config="groupConfig"
    @click="handleClick"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <v-rect :config="bgConfig" />
    <!-- Checkbox: stylized box with X -->
    <template v-if="isCheckbox">
      <v-rect :config="checkboxBoxConfig" />
      <v-line :config="checkboxX1Config" />
      <v-line :config="checkboxX2Config" />
    </template>
    <v-text :config="labelConfig" />
    <v-circle v-if="showStatusIndicator" :config="statusIndicatorConfig" />
  </v-group>
</template>

<script>
import {
  getSignerColor, FIELD_TYPES, ptToPixels, pixelsToPt, clamp,
  FIELD_FONT_FAMILY, FIELD_LEFT_PADDING_PT, FIELD_BASELINE_OFFSET_RATIO,
  computeFieldFontSize, TEXT_FIELD_TYPES
} from './fieldConstants.js';

export default {
  name: 'FieldRectangle',
  props: {
    field: { type: Object, required: true },
    scale: { type: Number, default: 1 },
    pageWidthPt: { type: Number, required: true },
    pageHeightPt: { type: Number, required: true },
    editable: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    status: { type: String, default: 'idle' },
  },
  emits: ['select', 'move'],
  computed: {
    color() {
      return getSignerColor(this.field.colorIndex ?? this.field.signerIndex ?? 0);
    },
    fieldDef() {
      return FIELD_TYPES[this.field.type] || FIELD_TYPES.Text;
    },
    displayX() {
      return ptToPixels(this.field.x, this.scale);
    },
    displayY() {
      return ptToPixels(this.field.y, this.scale);
    },
    displayWidth() {
      // Defensive fallback only - every persisted field has widthPt set,
      // either from the create-time resolver in FieldEditor or from the
      // server response. The 100/20 numbers are last-resort rendering
      // values, not real defaults; the server resolver and the drop
      // handler are the actual sources of truth for sizing.
      return ptToPixels(this.field.widthPt || 100, this.scale);
    },
    displayHeight() {
      return ptToPixels(this.field.heightPt || 20, this.scale);
    },
    stageWidth() {
      return ptToPixels(this.pageWidthPt, this.scale);
    },
    stageHeight() {
      return ptToPixels(this.pageHeightPt, this.scale);
    },
    groupConfig() {
      const self = this;
      return {
        x: this.displayX,
        y: this.displayY,
        width: this.displayWidth,
        height: this.displayHeight,
        scaleX: 1,
        scaleY: 1,
        draggable: this.editable,
        id: this.field.id,
        dragBoundFunc: this.editable
          ? function (pos) {
              // Use actual node dimensions (accounts for transformer resize)
              const nodeW = this.width() * this.scaleX();
              const nodeH = this.height() * this.scaleY();
              return {
                x: clamp(pos.x, 0, self.stageWidth - nodeW),
                y: clamp(pos.y, 0, self.stageHeight - nodeH),
              };
            }
          : undefined,
      };
    },
    isNote() {
      return this.field.type === 'Note';
    },
    bgConfig() {
      const isNote = this.isNote;
      return {
        x: 0,
        y: 0,
        width: this.displayWidth,
        height: this.displayHeight,
        fill: isNote ? 'rgba(158, 158, 158, 0.12)' : this.color.bg,
        stroke: this.selected ? (isNote ? '#616161' : this.color.text) : (isNote ? '#9E9E9E' : this.color.border),
        strokeWidth: this.selected ? 2 : 1,
        dash: isNote ? [4, 3] : undefined,
        cornerRadius: 2,
      };
    },
    showStatusIndicator() {
      return this.status === 'saving' || this.status === 'queued' || this.status === 'error';
    },
    statusIndicatorConfig() {
      const radius = Math.max(3, Math.min(5, this.displayHeight * 0.15));
      const colors = { saving: '#FF9800', queued: '#FF9800', error: '#F44336' };
      return {
        x: this.displayWidth - radius - 2,
        y: radius + 2,
        radius,
        fill: colors[this.status] || 'transparent',
        listening: false,
      };
    },
    isCheckbox() {
      return this.field.type === 'Checkbox';
    },
    checkboxBoxSize() {
      return Math.round(this.displayHeight * 0.7);
    },
    checkboxBoxConfig() {
      const size = this.checkboxBoxSize;
      return {
        x: 3,
        y: Math.round((this.displayHeight - size) / 2),
        width: size,
        height: size,
        stroke: this.color.text,
        strokeWidth: Math.max(1, Math.round(size * 0.1)),
        cornerRadius: 1,
        listening: false,
      };
    },
    checkboxX1Config() {
      const size = this.checkboxBoxSize;
      const bx = 3;
      const by = Math.round((this.displayHeight - size) / 2);
      const inset = Math.round(size * 0.2);
      return {
        points: [bx + inset, by + inset, bx + size - inset, by + size - inset],
        stroke: this.color.text,
        strokeWidth: Math.max(1, Math.round(size * 0.12)),
        lineCap: 'round',
        listening: false,
      };
    },
    checkboxX2Config() {
      const size = this.checkboxBoxSize;
      const bx = 3;
      const by = Math.round((this.displayHeight - size) / 2);
      const inset = Math.round(size * 0.2);
      return {
        points: [bx + size - inset, by + inset, bx + inset, by + size - inset],
        stroke: this.color.text,
        strokeWidth: Math.max(1, Math.round(size * 0.12)),
        lineCap: 'round',
        listening: false,
      };
    },
    labelConfig() {
      const heightPt = this.field.heightPt || 20;
      const isTextField = TEXT_FIELD_TYPES.has(this.field.type);

      // Text fields: canonical sizing that matches PDF burn output.
      // Non-text fields (Signature, Initials, Checkbox): centered label.
      const fontSizePt = isTextField
        ? computeFieldFontSize(heightPt)
        : clamp(heightPt * 0.6, 10, 14);
      const fontSize = ptToPixels(fontSizePt, this.scale);

      let label = this.field.type === 'Note'
        ? (this.field.defaultValue || this.field.label || this.fieldDef.label)
        : (this.field.label || this.fieldDef.label);
      if (this.field.required) label += ' *';

      const leftPaddingPx = isTextField
        ? ptToPixels(FIELD_LEFT_PADDING_PT, this.scale)
        : 4;
      const labelX = this.isCheckbox ? 3 + this.checkboxBoxSize + 4 : leftPaddingPx;
      const labelWidth = Math.max(0, this.displayWidth - labelX - leftPaddingPx);

      // Text fields: bottom-aligned with baseline offset (matches burned PDF).
      // Non-text fields: vertically centered.
      const yPos = isTextField
        ? Math.max(0, this.displayHeight - fontSize - (this.displayHeight * FIELD_BASELINE_OFFSET_RATIO))
        : (this.displayHeight - fontSize) / 2;

      return {
        x: labelX,
        y: yPos,
        text: label,
        fontSize,
        fontFamily: FIELD_FONT_FAMILY,
        fill: this.isNote ? '#424242' : this.color.text,
        width: labelWidth,
        ellipsis: true,
        wrap: 'none',
        listening: false,
      };
    },
  },
  methods: {
    /** Expose the Konva Group node for transformer attachment. */
    getKonvaNode() {
      return this.$refs.fieldGroup?.getNode?.();
    },
    handleClick(e) {
      e.cancelBubble = true;
      this.$emit('select', this.field.id);
    },
    handleDragStart() {
      this.$emit('select', this.field.id);
    },
    handleDragEnd(e) {
      const node = e.target;
      this.$emit('move', {
        id: this.field.id,
        x: clamp(pixelsToPt(node.x(), this.scale), 0, this.pageWidthPt),
        y: clamp(pixelsToPt(node.y(), this.scale), 0, this.pageHeightPt),
      });
    },
  },
};
</script>
